import { useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';

/**
 * Hook to manage camera operations for evidence and receipt capture.
 */
export const useCamera = () => {
    // Main camera state (for evidence modal)
    const [cameraMode, setCameraMode] = useState(false);
    const [cameraStream, setCameraStream] = useState(null);
    const [capturedImage, setCapturedImage] = useState(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    // Form camera state (for emission form receipts)
    const [formCameraMode, setFormCameraMode] = useState(false);
    const [formCameraStream, setFormCameraStream] = useState(null);
    const [formCapturedImage, setFormCapturedImage] = useState(null);
    const formVideoRef = useRef(null);
    const formCanvasRef = useRef(null);

    const startCamera = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment' }
            });
            setCameraStream(stream);
            setCameraMode(true);
            setCapturedImage(null);

            // Short delay to ensure video element is mounted
            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                }
            }, 100);
        } catch (err) {
            console.error('Camera error:', err);
            toast.error('Could not access camera. Please check permissions.');
        }
    }, []);

    const stopCamera = useCallback(() => {
        if (cameraStream) {
            cameraStream.getTracks().forEach(track => track.stop());
            setCameraStream(null);
        }
        setCameraMode(false);
    }, [cameraStream]);

    const capturePhoto = useCallback(() => {
        if (!videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);

        canvas.toBlob((blob) => {
            if (blob) {
                setCapturedImage(blob);
                if (cameraStream) {
                    cameraStream.getTracks().forEach(track => track.stop());
                    setCameraStream(null);
                }
            }
        }, 'image/jpeg', 0.9);
    }, [cameraStream]);

    const retakePhoto = useCallback(() => {
        setCapturedImage(null);
        startCamera();
    }, [startCamera]);

    // Form camera functions
    const startFormCamera = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment' }
            });
            setFormCameraStream(stream);
            setFormCameraMode(true);
            setFormCapturedImage(null);

            setTimeout(() => {
                if (formVideoRef.current) {
                    formVideoRef.current.srcObject = stream;
                }
            }, 100);
        } catch (err) {
            console.error('Camera error:', err);
            toast.error('Could not access camera. Please check permissions.');
        }
    }, []);

    const stopFormCamera = useCallback(() => {
        if (formCameraStream) {
            formCameraStream.getTracks().forEach(track => track.stop());
            setFormCameraStream(null);
        }
        setFormCameraMode(false);
    }, [formCameraStream]);

    const captureFormPhoto = useCallback((onCapture) => {
        if (!formVideoRef.current || !formCanvasRef.current) return;

        const video = formVideoRef.current;
        const canvas = formCanvasRef.current;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);

        canvas.toBlob((blob) => {
            if (blob) {
                const file = new File([blob], `receipt_${Date.now()}.jpg`, { type: 'image/jpeg' });
                setFormCapturedImage(blob);

                if (onCapture) onCapture(file, blob);

                if (formCameraStream) {
                    formCameraStream.getTracks().forEach(track => track.stop());
                    setFormCameraStream(null);
                }
                setFormCameraMode(false);
            }
        }, 'image/jpeg', 0.9);
    }, [formCameraStream]);

    const retakeFormPhoto = useCallback(() => {
        setFormCapturedImage(null);
        startFormCamera();
    }, [startFormCamera]);

    return {
        // Evidence camera
        cameraMode,
        cameraStream,
        capturedImage,
        videoRef,
        canvasRef,
        setCameraMode,
        setCapturedImage,
        startCamera,
        stopCamera,
        capturePhoto,
        retakePhoto,

        // Form camera
        formCameraMode,
        formCameraStream,
        formCapturedImage,
        formVideoRef,
        formCanvasRef,
        setFormCameraMode,
        setFormCapturedImage,
        startFormCamera,
        stopFormCamera,
        captureFormPhoto,
        retakeFormPhoto
    };
};
